import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useSpring, animated } from '@react-spring/web';
import { useTranslation } from 'next-i18next';

import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { cn, useModal } from '@restworld/utils-common';
import { Heading, Button } from '@restworld/ui-ds-base';

import ProfileEditModal, { IEditFormContentProps } from './profile-edit/profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../global-state.worker-data';

export interface CardWrapperProps extends IEditFormContentProps {
  children: React.ReactNode;
  title: string;
  variant?: 'edit' | 'add';
}
export const CardWrapper = ({ children, title, editPageType, showAlert, variant = 'edit' }: CardWrapperProps) => {
  const { show: showModal, open: openModal, close: closeModal } = useModal();
  const { data: workerFieldOptions } = useAtomValue(getWorkerFieldOptionsAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);

  const addFormBodyStyles = useSpring({
    opacity: showModal && variant === 'add' ? 1 : 0,
    transform: showModal && variant === 'add' ? 'translateY(0px)' : 'translateY(15px)',
    config: { tension: 250, friction: 20 },
    from: { opacity: 0, transform: 'translateY(15px)' },
  });

  const editFormBodyStyles = useSpring({
    opacity: showModal ? 1 : 0,
    transform: showModal ? 'translateY(0px)' : 'translateY(15px)',
    config: { tension: 250, friction: 20 },
    from: { opacity: 0, transform: 'translateY(15px)' },
  });

  const viewContentStyles = useSpring({
    opacity: !showModal ? 1 : 0,
    config: { tension: 250, friction: 20 },
    transform: !showModal ? 'translateY(0px)' : 'translateY(15px)',
    from: { opacity: 0, transform: 'translateY(15px)' },
  });

  const modalEl = (
    <ProfileEditModal
      showModal={showModal}
      onClose={closeModal}
      editPageType={editPageType}
      editPageTitle={title}
      showAlert={showAlert}
      key={title}
      mode="add"
    />
  );
  const containsEveryElement = useMemo(() => {
    if (['languages', 'education'].includes(editPageType) && variant === 'add') {
      if (editPageType === 'languages')
        return workerFieldOptions?.languages
          .map((v) => v.id)
          .every((v) => workerData?.languages.map((v) => `${v.language}`).includes(v));
      if (editPageType === 'education')
        return workerFieldOptions?.educationTitle
          .map((v) => v.id)
          .every((v) => workerData?.education_em.map((v) => v.education_title).includes(v));
    }
    return false;
  }, [
    editPageType,
    variant,
    workerData?.education_em,
    workerData?.languages,
    workerFieldOptions?.educationTitle,
    workerFieldOptions?.languages,
  ]);
  return (
    <Card>
      {!showModal ? (
        <div className="flex box-border flex-row items-center justify-between w-full p-0">
          <>
            <Heading className="leading-7.5">{title}</Heading>
            {variant === 'edit' && <EditButton onClick={openModal} />}
            {variant === 'add' && (
              <AddButton
                disabled={containsEveryElement}
                onClick={() => {
                  openModal();
                }}
              />
            )}
          </>
        </div>
      ) : null}
      {['work_experience', 'languages', 'education', 'certifications'].includes(editPageType) &&
      variant === 'add' &&
      showModal ? (
        <animated.div style={addFormBodyStyles} className="w-full">
          {modalEl}
          {children}
        </animated.div>
      ) : showModal ? (
        <animated.div style={editFormBodyStyles} className="w-full">
          {modalEl}
        </animated.div>
      ) : (
        <animated.div style={viewContentStyles} className="w-full">
          {children}
        </animated.div>
      )}
    </Card>
  );
};

export const Card = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex box-border flex-col w-full bg-primary-50 rounded-3.75 lg:p-8 p-4 overflow-hidden', className)}
    {...props}
  />
);

export const EditButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Button onClick={onClick} variant="tertiary" StartIcon={PencilIcon} className={'focus:ring-0'}>
      {t('common:edit')}
    </Button>
  );
};

export const AddButton = ({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) => {
  const { t } = useTranslation();
  return (
    <Button onClick={onClick} variant="primary" StartIcon={PlusIcon} disabled={disabled}>
      {t('common:add')}
    </Button>
  );
};
